import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { Modal } from "reactstrap";
import { Moment } from "moment";
import "moment/locale/ar";
import styles from "./styles.module.sass";
import { ChevronDownIcon } from "../../../../../../../assets/icons";
import QuestionIcon from "../../../../../../../assets/Question.svg";
import ClockIcon from "../../../../../../../assets/ic_clock.svg";
import i18n from "../../../../../../../i18n/i18n";
import CustomDateTimePicker from "../CustomDateTimePicker";
import {
  DURATION_PRESETS,
  validateActionModalForm,
  ActionModalReasonOption,
  getDurationDisplayLabel,
  getAutoResumeHint,
  isDurationBeforeMinDate,
} from "../../lib/serviceDisableModalUtils";
import { DisableDurationPreset } from "../../../../../../../types/audit";

export type { ActionModalReasonOption };

export interface ActionModalConfirmPayload {
  reason: string;
  duration?: DisableDurationPreset | null;
  customDate?: Moment | null;
}

export const defaultReasonOptions: ActionModalReasonOption[] = [
  { key: "pos_issue", label: "POS issue" },
  { key: "staff_shortage", label: "Staff shortage" },
  { key: "ramadan_hours", label: "Ramadan hours" },
  { key: "maintenance", label: "Maintenance" },
  { key: "brand_request", label: "Brand request" },
  { key: "other", label: "Other - Please specify" },
];

interface ActionModalProps {
  visible: boolean;
  title: string;
  subtitle?: string;
  reasonRequired?: boolean;
  reasonOptions?: ActionModalReasonOption[];
  reasonPlaceholder?: string;
  reasonLabel?: string;
  specifyPlaceholder?: string;
  reasonRequiredError?: string;
  customDateRequiredError?: string;
  initialReasonKey?: string;
  initialCustomReasonText?: string;
  initialDuration?: DisableDurationPreset;
  initialCustomDate?: Moment | null;
  confirmText: string;
  cancelText?: string;
  showDuration?: boolean;
  durationLabel?: string;
  durationHint?: string;
  resumeServiceName?: string;
  extraFooterButton?: { text: string; onClick: () => void };
  warningText?: string;
  minDate?: Moment | null;
  minDateError?: string;
  onCancel: () => void;
  onConfirm: (payload: ActionModalConfirmPayload) => void;
  modalClassName?: string;
  modalWidth?: number;
}

const ActionModal: React.FC<ActionModalProps> = ({
  visible,
  title,
  subtitle,
  reasonRequired = true,
  reasonOptions = defaultReasonOptions,
  reasonPlaceholder = "Select a reason",
  reasonLabel = "Reason",
  specifyPlaceholder = "Specify reason",
  reasonRequiredError = "Please select a reason",
  customDateRequiredError = "Please select a date and time",
  initialReasonKey = "",
  initialCustomReasonText = "",
  initialDuration = "permanent",
  initialCustomDate = null,
  confirmText,
  cancelText = "Cancel",
  showDuration = false,
  durationLabel = "Duration",
  durationHint,
  resumeServiceName,
  extraFooterButton,
  warningText,
  minDate,
  minDateError,
  onCancel,
  onConfirm,
  modalClassName = "serviceDisableModal",
  modalWidth = 860,
}) => {
  const [reasonKey, setReasonKey] = useState(initialReasonKey);
  const [customReasonText, setCustomReasonText] = useState(initialCustomReasonText);
  const [durationKey, setDurationKey] = useState<DisableDurationPreset>(initialDuration);
  const [customDate, setCustomDate] = useState<Moment | null>(initialCustomDate);
  const [isReasonOpen, setIsReasonOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [error, setError] = useState("");
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [datePickerStyle, setDatePickerStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const customPillRef = useRef<HTMLButtonElement>(null);
  const datePopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;

    setReasonKey(initialReasonKey);
    setCustomReasonText(initialCustomReasonText);
    const minDateMs = minDate?.valueOf();
    const durationIsTooEarly = isDurationBeforeMinDate(initialDuration, minDateMs);
    setDurationKey(durationIsTooEarly ? "custom" : initialDuration);
    const nextCustomDate =
      durationIsTooEarly || (minDate && initialCustomDate && initialCustomDate.isBefore(minDate))
        ? minDate || initialCustomDate
        : initialCustomDate;
    setCustomDate(nextCustomDate);
    setIsReasonOpen(false);
    setIsDatePickerOpen(false);
    setError("");
  }, [
    visible,
    initialReasonKey,
    initialCustomReasonText,
    initialDuration,
    initialCustomDate,
    minDate,
  ]);

  const updatePopoverPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 6;
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;

    setPopoverStyle({
      position: "fixed",
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(120, spaceBelow - gap),
      zIndex: 9999,
    });
  }, []);

  const updateDatePickerPosition = useCallback(() => {
    if (!customPillRef.current) return;

    const rect = customPillRef.current.getBoundingClientRect();
    const viewportPadding = 8;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openBelow = spaceBelow >= spaceAbove;
    const maxHeight = Math.max(200, (openBelow ? spaceBelow : spaceAbove) - gap);

    setDatePickerStyle({
      position: "fixed",
      top: openBelow ? rect.bottom + gap : Math.max(viewportPadding, rect.top - maxHeight - gap),
      left: rect.left,
      maxHeight,
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    if (isReasonOpen) {
      updatePopoverPosition();
      window.addEventListener("scroll", updatePopoverPosition, true);
      window.addEventListener("resize", updatePopoverPosition);
    }
    return () => {
      window.removeEventListener("scroll", updatePopoverPosition, true);
      window.removeEventListener("resize", updatePopoverPosition);
    };
  }, [isReasonOpen, updatePopoverPosition]);

  useEffect(() => {
    if (isDatePickerOpen) {
      updateDatePickerPosition();
      window.addEventListener("scroll", updateDatePickerPosition, true);
      window.addEventListener("resize", updateDatePickerPosition);
    }
    return () => {
      window.removeEventListener("scroll", updateDatePickerPosition, true);
      window.removeEventListener("resize", updateDatePickerPosition);
    };
  }, [isDatePickerOpen, updateDatePickerPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (isReasonOpen) {
        if (
          triggerRef.current &&
          !triggerRef.current.contains(target) &&
          popoverRef.current &&
          !popoverRef.current.contains(target)
        ) {
          setIsReasonOpen(false);
        }
      }

      if (isDatePickerOpen) {
        if (
          customPillRef.current &&
          !customPillRef.current.contains(target) &&
          datePopoverRef.current &&
          !datePopoverRef.current.contains(target)
        ) {
          setIsDatePickerOpen(false);
        }
      }
    };

    if (isReasonOpen || isDatePickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isReasonOpen, isDatePickerOpen]);

  const selectedReasonLabel =
    reasonOptions.find((option) => option.key === reasonKey)?.label || "";

  const getReasonText = () => {
    if (reasonKey === "other") {
      return customReasonText.trim();
    }
    return selectedReasonLabel;
  };

  const isSaveDisabled = useMemo(() => {
    if (reasonRequired && !reasonKey) {
      return true;
    }

    if (reasonKey === "other" && !customReasonText.trim()) {
      return true;
    }

    if (showDuration && durationKey === "custom" && !customDate) {
      return true;
    }

    return false;
  }, [
    reasonRequired,
    reasonKey,
    customReasonText,
    showDuration,
    durationKey,
    customDate,
  ]);

  const resolvedDurationHint = getAutoResumeHint({
    serviceName: resumeServiceName,
    duration: durationKey,
    customDate,
    fallbackHint: durationHint,
  });

  const handleConfirm = () => {
    const validationError = validateActionModalForm({
      reasonRequired,
      reasonKey,
      customReasonText,
      showDuration,
      durationKey,
      customDate,
      reasonRequiredError,
      customDateRequiredError,
      minDate,
      minDateError,
    });

    if (validationError) {
      setError(validationError);
      return;
    }

    onConfirm({
      reason: getReasonText(),
      duration: showDuration ? durationKey : "permanent",
      customDate: showDuration && durationKey === "custom" ? customDate : null,
    });
  };

  const handleDurationSelect = (preset: DisableDurationPreset) => {
    if (isDurationBeforeMinDate(preset, minDate?.valueOf())) {
      return;
    }
    setDurationKey(preset);
    setError("");

    if (preset === "custom") {
      if (minDate && (!customDate || customDate.isBefore(minDate))) {
        setCustomDate(minDate);
      }
      setIsDatePickerOpen(true);
    } else {
      setIsDatePickerOpen(false);
    }
  };

  const reasonPopover = isReasonOpen && (
    <div ref={popoverRef} style={popoverStyle} className={styles.reasonPopover}>
      {reasonOptions.map((option) => (
        <button
          key={option.key}
          type="button"
          className={`${styles.reasonOption} ${reasonKey === option.key ? styles.reasonOptionSelected : ""
            }`}
          onClick={() => {
            setReasonKey(option.key);
            setError("");
            setIsReasonOpen(false);
          }}
        >
          <span
            className={`${styles.radio} ${reasonKey === option.key ? styles.radioSelected : ""
              }`}
          />
          <span className={styles.reasonOptionText}>{option.label}</span>
        </button>
      ))}
    </div>
  );

  const datePopover =
    isDatePickerOpen &&
    durationKey === "custom" &&
    ReactDOM.createPortal(
      <div ref={datePopoverRef} className={styles.datePickerPopover} style={datePickerStyle}>
        <CustomDateTimePicker
          value={customDate}
          minDate={minDate}
          onApply={(value) => {
            setCustomDate(value);
            setIsDatePickerOpen(false);
            setError("");
          }}
          onCancel={() => setIsDatePickerOpen(false)}
          cancelLabel={i18n.t("serviceDisableModal:cancel")}
          applyLabel={i18n.t("serviceDisableModal:apply")}
        />
      </div>,
      document.body
    );

  return (
    <Modal
      isOpen={visible}
      toggle={onCancel}
      centered
      className={modalClassName}
      style={{ "--modal-width": `${modalWidth}px` } as React.CSSProperties}
    >
      <div className={styles.modal} dir={i18n.language === "ar" ? "rtl" : "ltr"}>
        <div className={styles.titleSection}>
          <span className={styles.title}>{title}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>

        {showDuration && (
          <div className={styles.durationSectionCard}>
            <div className={styles.durationSectionHeader}>
              <img
                src={ClockIcon}
                alt="Duration"
                className={styles.durationSectionIcon}
              />
              <span className={styles.durationSectionTitle}>{durationLabel}</span>
            </div>

            {resolvedDurationHint && (
              <p className={styles.durationHint}>{resolvedDurationHint}</p>
            )}

            <div className={styles.durationPills}>
              {DURATION_PRESETS.map((preset) => {
                const isPresetDisabled = isDurationBeforeMinDate(preset, minDate?.valueOf());
                return (
                <button
                  key={preset}
                  ref={preset === "custom" ? customPillRef : undefined}
                  type="button"
                  disabled={isPresetDisabled}
                  className={`${styles.durationPill} ${durationKey === preset ? styles.durationPillSelected : ""
                    } ${isPresetDisabled ? styles.durationPillDisabled : ""}`}
                  onClick={() => handleDurationSelect(preset)}
                >
                  {getDurationDisplayLabel(
                    preset,
                    preset === durationKey ? customDate : null
                  )}
                </button>
                );
              })}
            </div>
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <img src={QuestionIcon} alt="Question" className={styles.questionIcon} />
            <span className={styles.sectionTitle}>{reasonLabel}</span>
          </div>
          <button
            ref={triggerRef}
            type="button"
            className={`${styles.reasonTrigger} ${!reasonKey ? styles.reasonTriggerPlaceholder : ""
              }`}
            onClick={() => setIsReasonOpen((open) => !open)}
          >
            <span>{reasonKey ? selectedReasonLabel : reasonPlaceholder}</span>
            <span className={isReasonOpen ? styles.caretUp : styles.caretDown}>
              <ChevronDownIcon width="18" height="18" stroke="#65656C" />
            </span>
          </button>

          {reasonKey === "other" && (
            <textarea
              className={styles.specifyTextarea}
              placeholder={specifyPlaceholder}
              value={customReasonText}
              onChange={(e) => {
                setCustomReasonText(e.target.value);
                if (e.target.value.trim()) {
                  setError("");
                }
              }}
            />
          )}
        </div>

        {warningText && (
          <div className={styles.warningText}>
            <img
              src={require("../../../../../../../assets/ic_info.svg").default}
              alt="info"
              className={styles.warningIcon}
            />
            <span>{warningText}</span>
          </div>
        )}

        {error && <div className={styles.errorText}>{error}</div>}

        <div className={styles.footer}>
          <button type="button" className={styles.cancelBtn} onClick={onCancel}>
            {cancelText}
          </button>
          {extraFooterButton && (
            <button
              type="button"
              className={styles.enableBtn}
              onClick={extraFooterButton.onClick}
            >
              {extraFooterButton.text}
            </button>
          )}
          <button
            type="button"
            className={`${styles.saveBtn} ${isSaveDisabled ? styles.saveBtnDisabled : ""
              }`}
            disabled={isSaveDisabled}
            onClick={handleConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>

      {ReactDOM.createPortal(reasonPopover, document.body)}
      {datePopover}
    </Modal>
  );
};

export default ActionModal;
